import styled, { x } from '@xstyled/styled-components';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import {
  LeftMatchItem,
  QuizMatchStore,
  RightMatchItem,
  useQuizMatchStore
} from '../QuizMatchStore';
// import { CardButton } from '~/training/sequence/templates/items/quiz-match/components/CardButton';
import { BoxButton } from '~/training/sequence/templates/items/quiz-match/components/BoxButton';

const FoldersGrid = styled.div<{ itemCount: number }>`
  display: grid !important;
  grid-template-columns: repeat(4, 1fr) !important;
  gap: 40px !important;
  padding: 0 !important;
  justify-content: center;
  justify-items: center;

  /* For grids with fewer than 4 items, use auto-fit to center them */
  ${props =>
    props.itemCount < 4 &&
    `
    grid-template-columns: repeat(${props.itemCount}, auto) !important;
    justify-content: center !important;
  `}

  /* For 5 folders: center the last one in second row */
  ${props =>
    props.itemCount === 5 &&
    `
    > *:nth-child(5) {
      grid-column: 2 / 4 !important;
      justify-self: center !important;
    }
  `}

  /* For 6 items: center the last two in second row */
  ${props =>
    props.itemCount === 6 &&
    `
    > *:nth-child(5) {
      grid-column: 2 !important;
      justify-self: center !important;
    }
    > *:nth-child(6) {
      grid-column: 3 !important;
      justify-self: center !important;
    }
  `}

  /* For 7 items: center folders 5 and 6 */
  ${props =>
    props.itemCount === 7 &&
    `
    > *:nth-child(5) {
      grid-column: 2 !important;
      justify-self: center !important;
    }
    > *:nth-child(6) {
      grid-column: 3 !important;
      justify-self: center !important;
    }
    > *:nth-child(7) {
      grid-column: auto !important;
      display: none !important;
    }
  `}

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr) !important;

    /* Reset desktop-specific positioning on mobile */
    ${props =>
      (props.itemCount === 5 ||
        props.itemCount === 6 ||
        props.itemCount === 7) &&
      `
      > *:nth-child(5),
      > *:nth-child(6),
      > *:nth-child(7) {
        grid-column: auto !important;
        justify-self: center !important;
        display: block !important; /* Override the display: none for 7th item */
      }
    `}
  }

  /* Each grid item centered */
  > * {
    justify-self: center;
  }
`;

const selector = (s: QuizMatchStore) => select(s, 'leftItems', 'rightItems');
const sequenceSelector = (s: SequenceStore) =>
  select(s, 'addBlocker', 'removeBlocker', 'blockers');

const QuizMatchUIBlock = styled.div`
  width: 100%;
  height: auto;
  .topPart {
    height: 165px;
    opacity: 0.5;

    .CardBorder {
      // box-shadow: -6px 6px 6px grey;
      padding: 15px;
      z-index: 200;
      background-color: white;
      border-radius: xl;
      min-height: 160px;
      max-height: 220px;
      width: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      margin-left: auto;
      margin-right: auto;
      font-weight: bolder;
      font-family: sans-serif;
      font-size: 14px;
      line-height: 1.3;
      word-break: break-word;
      overflow-wrap: break-word;
      overflow: hidden;
      position: relative;

      /* Use text-overflow instead of webkit-line-clamp for better centering */
      white-space: pre-wrap;
    }

    .CardBorder:last-child {
      margin-top: -7px;
    }
  }
  .bottomPart {
    display: flex;
    flex-wrap: wrap;
    margin-top: 65px;
  }
  @media (max-width: sm) {
    .bottomPart {
      margin-top: 50px;
      display: grid;
      grid-template: 50px 50px !important;
      grid-template-columns: 1fr 1fr;
      .BoxBorder {
        width: 100%;
        min-height: 130px;
        position: relative;

        .BoxShape {
          position: inherit;
          display: flex;
          align-items: center;
          justify-content: center;
          padding-top: 45px;
          padding-bottom: 20px;
          text-align: center;
          width: 100%;
          height: 97%;
          div {
            margin-top: -13px;
          }
        }
      }
    }
  }
`;

export const QuizMatchBlockRoot: FC<{ id: string }> = ({ id }) => {
  const cardBorderRef = useRef<HTMLDivElement>(null);
  const mobileMatch = useQuizMatchStore(s => s.mobileMatch);
  const { leftItems, rightItems } = useQuizMatchStore(selector, shallow);
  const { sendRxdInteractions } = useSequenceStore();
  const [lastLeftId, setLastLeftId] = useState('0');
  const _oneLastElement = leftItems
    .filter(i => (!('show' in i) || i.show ? true : false))
    .pop();

  const { addBlocker, removeBlocker, blockers } = useSequenceStore(
    sequenceSelector,
    shallow
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let RXD: any;

  try {
    if (window.document.referrer) {
      const preUrl = window?.document?.referrer;
      const domain = preUrl?.split('/')[2];

      if (domain !== 'localhost:8000' && domain !== 'www.easyllama.com') {
        RXD = (window.parent as any).RXD;
      }
    } else {
      RXD = (window.parent as any).RXD;
    }
  } catch (error) {
    console.error('RXD in BTN:', error);
  }

  useEffect(() => {
    if (_oneLastElement && leftItems) {
      setLastLeftId(_oneLastElement?.pairId);
    }
  }, [leftItems]);

  useEffect(() => {
    if (
      !rightItems.find(el => !(el.leftItem && el.leftItem.pairId == el.pairId))
    )
      return;
    addBlocker(id);
    return () => removeBlocker(id);
  }, [leftItems, lastLeftId]);

  const onClickLeftButton = (id: string) => {
    setLastLeftId(id);
  };

  const onClickRightButton = (
    leftElement: LeftMatchItem,
    rightElement: RightMatchItem
  ) => {
    if (lastLeftId && lastLeftId !== '0') {
      mobileMatch(leftElement, rightElement);
      setLastLeftId('0');
      cardBorderRef.current?.focus();

      const correctElement = rightItems.find(
        el => el.pairId == leftElement.pairId
      );

      const answer = rightElement.label.trim();
      const question = `Match the correct description to "${leftElement.label.trim()}"?`;

      if (correctElement && sendRxdInteractions && RXD) {
        const correctAnswer = correctElement.label;

        try {
          RXD.RecordMultipleChoiceInteraction(
            `${id}.${leftElement.pairId}`,
            answer,
            leftElement.pairId == rightElement.pairId,
            correctAnswer,
            question
          );
        } catch (error) {
          console.error('Error in RecordMultipleChoiceInteraction:', error);
        }
      }
    }
  };

  useEffect(() => {
    if (lastLeftId == '0') {
      const lastElement = leftItems
        .filter(i => (!('show' in i) || i.show ? true : false))
        .pop();
      if (lastElement) {
        setLastLeftId(lastElement.pairId);
      }
    }
  }, [lastLeftId]);

  const leftListItems = useMemo(() => {
    const items = leftItems.filter(i => i.show !== false) || [];
    return items ? [items.pop()] : [];
  }, [leftItems, lastLeftId]);

  return (
    <QuizMatchUIBlock>
      <div className="topPart">
        {leftListItems?.map((i, key) => {
          return i ? (
            <div
              className="CardBorder cardBorderStyleForDarkTheme"
              ref={cardBorderRef}
              key={`CardBorder_${key}`}
              draggable
              onDragStart={e => {
                e.dataTransfer.setData(
                  'text/plain',
                  JSON.stringify({ label: i.label, pairId: i.pairId })
                );
                e.dataTransfer.dropEffect = 'move';
              }}
            >
              {i.label}
            </div>
          ) : null;
        })}
      </div>
      <FoldersGrid
        itemCount={rightItems.length}
        style={{
          opacity: lastLeftId == '0' && blockers.length ? '0.5' : '1'
        }}
        className="bottomPart"
      >
        {rightItems.map(i => (
          <BoxButton
            disabled={Boolean(i.leftItem)}
            key={i.pairId}
            item={i}
            {...i}
            onClickLeftButton={onClickLeftButton}
            onClick={() => {
              onClickRightButton(
                leftItems.find(el => el.pairId == lastLeftId) as LeftMatchItem,
                i
              );
            }}
            onDragOver={e => {
              e.preventDefault();
            }}
            onDrop={e => {
              e.preventDefault();
              try {
                const dataTransferText = e.dataTransfer.getData('text/plain');
                if (!dataTransferText) {
                  return;
                }
                const draggedData = JSON.parse(dataTransferText);
                const leftElement = leftItems.find(
                  el => el.pairId === draggedData.pairId
                );
                if (leftElement) {
                  onClickRightButton(leftElement, i);
                }
              } catch (error) {
                // Silently ignore invalid drag data to prevent Sentry errors
              }
            }}
          />
        ))}
      </FoldersGrid>
    </QuizMatchUIBlock>
  );
};
